import React, { useState } from "react";
import { motion } from "framer-motion";

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (content: string, visibleToReseller: boolean) => void;
  orderId?: string;
  entityType?: "order" | "offer";
  showVisibilityOption?: boolean;
}

const AddNoteModal: React.FC<AddNoteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  orderId,
  entityType = "order",
  showVisibilityOption = true,
}) => {
  const [content, setContent] = useState("");
  const [visibleToReseller, setVisibleToReseller] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(content, visibleToReseller);
      setContent("");
      setVisibleToReseller(false);
      onClose();
    } catch (error) {
      console.error("Error adding note:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setContent("");
    setVisibleToReseller(false);
    onClose();
  };

  if (!isOpen) return null;

  const entityLabel = entityType === "order" ? "Encomenda" : "Proposta";

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={handleClose}></div>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="modal-card"
        style={{ maxWidth: "600px" }}
      >
        <header className="modal-card-head">
          <p className="modal-card-title">
            Adicionar Nota à {entityLabel} #{orderId}
          </p>
          <button
            className="delete"
            aria-label="close"
            onClick={handleClose}
            type="button"
          ></button>
        </header>
        <section className="modal-card-body">
          <div>
            <div className="field">
              <label className="label">
                Nota <span className="has-text-danger">*</span>
              </label>
              <div className="control">
                <textarea
                  className="textarea"
                  placeholder="Escreva a sua nota aqui..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={6}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <p className="help">Campo obrigatório</p>
            </div>

            {showVisibilityOption && (
              <div className="field">
                <div className="control">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={visibleToReseller}
                      onChange={(e) => setVisibleToReseller(e.target.checked)}
                      disabled={isSubmitting}
                    />
                    <span className="ml-2">Visível para o revendedor</span>
                  </label>
                </div>
                <p className="help">
                  Se marcado, o revendedor poderá ver esta nota
                </p>
              </div>
            )}
          </div>
        </section>
        <footer className="modal-card-foot">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="button is-primary"
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            type="button"
          >
            {isSubmitting ? "A adicionar..." : "Adicionar Nota"}
          </motion.button>
          <button
            className="button"
            onClick={handleClose}
            disabled={isSubmitting}
            type="button"
          >
            Fechar
          </button>
        </footer>
      </motion.div>
    </div>
  );
};

export default AddNoteModal;
